/**
 * YulOnTap Frontend Security Module
 * ==================================
 * Implements frontend security measures including:
 * - Role-based UI component visibility
 * - DevTools detection and blocking
 * - Copy/inspect protection for sensitive data
 * - Session timeout handling
 */

// Session timeout in minutes (must match backend)
export const SESSION_TIMEOUT_MINUTES = 15;

// Demo Setting (super admin only) — longer idle window while presenting.
export const DEMO_SESSION_TIMEOUT_MINUTES = 60;

// Check if running in production
export const isProduction = process.env.NODE_ENV === 'production';

/**
 * Disable React DevTools in production
 */
export const disableDevTools = () => {
    if (isProduction && typeof window !== 'undefined') {
        // Disable React DevTools
        if (typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === 'object') {
            for (let key in window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
                if (key !== 'renderers') {
                    window.__REACT_DEVTOOLS_GLOBAL_HOOK__[key] = typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__[key] === 'function'
                        ? () => {}
                        : null;
                }
            }
        }
    }
};

/**
 * Disable right-click context menu on sensitive elements
 */
export const disableContextMenu = (element) => {
    if (element) {
        element.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            return false;
        });
    }
};

/**
 * Disable text selection on sensitive elements
 */
export const disableSelection = (element) => {
    if (element) {
        element.style.userSelect = 'none';
        element.style.webkitUserSelect = 'none';
        element.style.msUserSelect = 'none';
        element.style.MozUserSelect = 'none';
    }
};

/**
 * Check if user has a specific permission based on security_config
 */
export const hasPermission = (securityConfig, permission) => {
    if (!securityConfig) return false;
    return securityConfig[permission] === true;
};

/**
 * Role-based visibility checks
 */
export const roleChecks = {
    canViewCards: (config) => hasPermission(config, 'can_view_cards'),
    canManageCards: (config) => hasPermission(config, 'can_manage_cards'),
    canViewCardUrls: (config) => hasPermission(config, 'can_view_card_urls'),
    canViewAnalytics: (config) => hasPermission(config, 'can_view_analytics'),
    canExportFull: (config) => hasPermission(config, 'can_export_full'),
    canExportSummary: (config) => hasPermission(config, 'can_export_summary'),
    canViewUsers: (config) => hasPermission(config, 'can_view_users'),
    canManageUsers: (config) => hasPermission(config, 'can_manage_users'),
    canViewAuditLogs: (config) => hasPermission(config, 'can_view_audit_logs'),
    canViewSystemConfig: (config) => hasPermission(config, 'can_view_system_config'),
    showInternalIds: (config) => hasPermission(config, 'show_internal_ids'),
};

/**
 * Session activity tracker
 */
let lastActivityTime = Date.now();
let sessionTimeoutCallback = null;

export const updateActivity = () => {
    lastActivityTime = Date.now();
};

export const checkSessionTimeout = (timeoutMinutes = SESSION_TIMEOUT_MINUTES) => {
    const elapsed = (Date.now() - lastActivityTime) / 1000 / 60; // minutes
    return elapsed >= timeoutMinutes;
};

export const setupActivityTracking = (onTimeout, timeoutMinutes = SESSION_TIMEOUT_MINUTES) => {
    sessionTimeoutCallback = onTimeout;
    // The clock restarts whenever the tracker is (re)installed — e.g. the Demo
    // Setting flipping on or off swaps 15 ⇄ 60 minutes without a reload.
    lastActivityTime = Date.now();

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
        document.addEventListener(event, updateActivity, true);
    });
    
    // Check for timeout every minute
    const interval = setInterval(() => {
        if (checkSessionTimeout(timeoutMinutes) && sessionTimeoutCallback) {
            sessionTimeoutCallback();
            clearInterval(interval);
        }
    }, 60000);
    
    return () => {
        clearInterval(interval);
        events.forEach(event => {
            document.removeEventListener(event, updateActivity, true);
        });
    };
};

/**
 * Sanitize display of sensitive data
 */
export const maskCardId = (cardId) => {
    // For non-super-admins, card IDs should never be shown
    // This is a fallback in case data leaks through
    if (!cardId) return '***';
    return `Card-${cardId.substring(0, 4)}***`;
};

export const maskEmail = (email) => {
    if (!email || !email.includes('@')) return '***@***';
    const [local, domain] = email.split('@');
    const maskedLocal = local.length <= 2 
        ? local[0] + '***' 
        : local[0] + '***' + local[local.length - 1];
    return `${maskedLocal}@${domain}`;
};

/**
 * Initialize all security measures
 */
export const initializeSecurity = () => {
    if (isProduction) {
        disableDevTools();
        
        // Disable keyboard shortcuts for DevTools
        document.addEventListener('keydown', (e) => {
            // F12
            if (e.key === 'F12') {
                e.preventDefault();
                return false;
            }
            // Ctrl+Shift+I (DevTools)
            if (e.ctrlKey && e.shiftKey && e.key === 'I') {
                e.preventDefault();
                return false;
            }
            // Ctrl+Shift+J (Console)
            if (e.ctrlKey && e.shiftKey && e.key === 'J') {
                e.preventDefault();
                return false;
            }
            // Ctrl+U (View Source)
            if (e.ctrlKey && e.key === 'u') {
                e.preventDefault();
                return false;
            }
        });
    }
};

export default {
    disableDevTools,
    disableContextMenu,
    disableSelection,
    hasPermission,
    roleChecks,
    updateActivity,
    checkSessionTimeout,
    setupActivityTracking,
    maskCardId,
    maskEmail,
    initializeSecurity,
    isProduction,
    SESSION_TIMEOUT_MINUTES,
};
